import { AddCircle } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SubmissionPrerequisites from '@/components/SubmissionPrerequesites';
import TimeMaskInput from '@/components/TimeMaskInput';
import { videoOutputs } from '@/consts';
import { useAuth } from '@/contexts/AuthContext';
import useLanguageDisplayNames from '@/hooks/useLanguageDisplayNames';
import { isValidVideoLink } from '@/utils/helper';

const defaultIncentive = { name: '', description: '', options: '', estimate: '' };

export default function SubmissionForm({ formData, events, setData, submit }) {
  const { t } = useTranslation();
  const { userData, socialAccounts } = useAuth();
  const [errors, setErrors] = useState(/** @type {{videoUrl: string}} */ ({}));
  const [readGuidelines, setReadGuidelines] = useState(false);
  const discordAccountLinked = socialAccounts.some(account => account.provider === 'discord');
  const displayNames = useLanguageDisplayNames();
  const isEdit = Boolean(formData.id);

  if (events.length === 1) {
    /* eslint-disable-next-line react-hooks/immutability --
    HACK: calling setData will unfortunately not work on initial render */
    formData.event = events[0].id;
  }

  const validateForm = () => {
    const newErrors = {};

    if (!isValidVideoLink(formData.videoUrl)) {
      newErrors.videoUrl = 'Invalid or incomplete video URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (validateForm()) {
      submit(event);
    }
  };

  const change = event => {
    const { name, value, type, checked } = event.target;
    setData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const incentiveAdd = () => {
    setData(prev => ({
      ...prev,
      incentives: [...prev.incentives, { ...defaultIncentive }],
    }));
  };

  const incentiveChange = (index, field, value) => {
    const updated = [...formData.incentives];
    updated[index][field] = value;
    setData(prev => ({ ...prev, incentives: updated }));
  };

  const incentiveRemove = index => {
    const updated = [...formData.incentives];
    updated.splice(index, 1);
    setData(prev => ({ ...prev, incentives: updated }));
  };

  const title = (
    <Typography gutterBottom variant="h2" className="font-bebas">
      {isEdit ? t('run.edit.title') : t('run.submit.title')}
    </Typography>
  );

  if (!userData.inDiscordGuild || !discordAccountLinked) {
    return (
      <>
        {title}
        <SubmissionPrerequisites
          inDiscordGuild={userData.inDiscordGuild}
          discordAccountLinked={!!discordAccountLinked}
        />
      </>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ marginTop: 2, paddingBottom: 4 }}>
      {title}
      {!isEdit && (
        <Typography variant="body1">
          {t('run.join.prefix')} <Link href="/events/join">{t('common.click.here')}</Link>{' '}
          {t('run.join.suffix')}
        </Typography>
      )}
      <FormControlLabel
        sx={{ my: 2 }}
        control={
          <Checkbox
            required
            checked={readGuidelines}
            onChange={event => setReadGuidelines(event.target.checked)}
            name="readGuidelines"
          />
        }
        label={
          <>
            {t('run.guidelines.confirmation')}{' '}
            <Link noWrap href="/events/guides/submission">
              {t('run.guidelines.linkLabel')}
            </Link>
          </>
        }
      />
      {readGuidelines && (
        <Grid
          container
          spacing={2}
          sx={{ marginTop: 2 }}
          // Actually disables inputs, but doesn't propagate a "disabled style" :/
        >
          {!isEdit && (
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel id="event-select-label">{t('common.event')}</InputLabel>
                <Select
                  labelId="event-select-label"
                  label={t('common.event')}
                  name="event"
                  value={formData.event}
                  onChange={change}
                >
                  {events.map(event => (
                    <MenuItem key={event.id} value={event.id}>
                      {event.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              label={t('run.name')}
              name="name"
              value={formData.name}
              onChange={change}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              label={t('run.category')}
              name="category"
              value={formData.category}
              onChange={change}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              label={t('run.estimate')}
              value={formData.estimate}
              name="estimate"
              onChange={change}
              helperText={t('run.estimate.helper')}
              slotProps={{
                input: { inputComponent: TimeMaskInput },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              label={t('run.platform')}
              name="platform"
              value={formData.platform}
              onChange={change}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              label={t('run.url')}
              error={!!errors.videoUrl}
              name="videoUrl"
              value={formData.videoUrl}
              onChange={change}
              helperText={t('run.url.helper')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              required
              fullWidth
              label={t('run.stationsCount')}
              name="stationsCount"
              value={formData.stationsCount}
              onChange={change}
              helperText={t('run.stationsCount.helper')}
              slotProps={{ htmlInput: { type: 'number', min: 1, max: 4, step: 1 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel id="networkAccess-label">{t('run.networkAccess')}</InputLabel>
              <Select
                labelId="networkAccess-label"
                id="networkAccess"
                name="networkAccess"
                value={formData.networkAccess}
                label={t('run.networkAccess')}
                onChange={change}
              >
                <MenuItem value="none">{t('run.networkAccess.none')}</MenuItem>
                <MenuItem value="local">{t('run.networkAccess.local')}</MenuItem>
                <MenuItem value="online">{t('run.networkAccess.online')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel id="videoOutput-label">{t('run.videoOutput')}</InputLabel>
              <Select
                labelId="videoOutput-label"
                id="videoOutput"
                name="videoOutput"
                value={formData.videoOutput}
                label={t('run.videoOutput')}
                onChange={change}
              >
                {videoOutputs.map(({ value, label, translationKey }) => (
                  <MenuItem key={value} value={value}>
                    {translationKey ? t(translationKey) : label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel id="commentaryLanguage-label">{t('run.commentaryLanguage')}</InputLabel>
              <Select
                labelId="commentaryLanguage-label"
                id="commentaryLanguage"
                name="commentaryLanguage"
                value={formData.commentaryLanguage}
                label={t('run.commentaryLanguage')}
                onChange={change}
              >
                <MenuItem value="fr">{displayNames.of('fr')}</MenuItem>
                <MenuItem value="en">{displayNames.of('en')}</MenuItem>
                <MenuItem value="both">{t('run.commentaryLanguage.both')}</MenuItem>
              </Select>
              <FormHelperText>{t('run.commentaryLanguage.helper')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              sx={{ marginTop: 1 }} // Align with other inputs text
              label={t('run.emu.label')}
              control={<Checkbox checked={formData.emulator} onChange={change} name="emulator" />}
            />
          </Grid>
          <Grid size={12}>
            <FormControlLabel
              label={t('run.race')}
              control={
                <Checkbox
                  disabled={isEdit}
                  checked={formData.isRace}
                  onChange={change}
                  name="isRace"
                />
              }
            />
            <FormControlLabel
              label={t('run.coop')}
              control={
                <Checkbox
                  disabled={isEdit}
                  checked={formData.isCoop}
                  onChange={change}
                  name="isCoop"
                />
              }
            />
          </Grid>
          <Grid size={12}>
            <TextField
              required
              fullWidth
              multiline
              label={t('run.comment')}
              name="comment"
              value={formData.comment}
              onChange={change}
              minRows={8}
            />
          </Grid>
          {formData.raceCode && (
            <Grid size={12}>
              <TextField
                required
                fullWidth
                label={t('run.code')}
                name="raceCode"
                value={formData.raceCode}
                onChange={change}
                helperText={t('run.code.helper')}
              />
            </Grid>
          )}
          <Grid size={12}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography gutterBottom variant="h6">
                {t('incentive.title')}
              </Typography>
              <Tooltip title={t('incentive.add')}>
                <IconButton onClick={incentiveAdd} size="small">
                  <AddCircle color="success" />
                </IconButton>
              </Tooltip>
            </Box>
            {formData.incentives?.map((incentive, index) => (
              /*
              We have no guaranteed existing and stable property for incentives
              They may be newly created (so no id), and using a form field
              property will cause re-renders and de-focus.
              Incentives shouldn't be re-orderable anyway.
              */
              <Box key={incentive.id ?? -index} marginBottom={2}>
                <Grid container spacing={1}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      required
                      label={t('common.name')}
                      value={incentive.name}
                      onChange={event => incentiveChange(index, 'name', event.target.value)}
                      helperText={t('incentive.name.helper')}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      label={t('common.description')}
                      value={incentive.description}
                      helperText={t('incentive.description.helper')}
                      minRows={4}
                      slotProps={{ htmlInput: { maxLength: 255 } }}
                      onChange={event => incentiveChange(index, 'description', event.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('common.options')}
                      value={incentive.options}
                      onChange={event => incentiveChange(index, 'options', event.target.value)}
                      helperText={t('incentive.options.helper')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('run.estimate')}
                      value={incentive.estimate}
                      onChange={event => incentiveChange(index, 'estimate', event.target.value)}
                      helperText={t('incentive.estimate.helper')}
                      slotProps={{
                        input: { inputComponent: TimeMaskInput },
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Button
                      onClick={() => incentiveRemove(index)}
                      color="error"
                      size="small"
                      variant="outlined"
                    >
                      {t('incentive.remove')}
                    </Button>
                  </Grid>
                </Grid>
                <Divider sx={{ marginTop: 2 }} />
              </Box>
            ))}
          </Grid>
          <Grid size={12}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              className="font-oswald"
              type="submit"
            >
              {t('common.submit')}
            </Button>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

SubmissionForm.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  formData: PropTypes.object.isRequired,
  setData: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
};
